import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import authStore from '../../store/auth';

/**
 * Parse a GFM markdown table block (array of raw lines) into a React <table>.
 * Lines look like: | col1 | col2 | and the second line is the separator.
 */
const parseTable = (lines, key) => {
  const parseRow = line =>
    line
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map(c => c.trim());

  const headers = parseRow(lines[0]);
  const bodyRows = lines.slice(2).filter(l => l.trim());

  return (
    <div key={key} style={{ overflowX: 'auto', margin: '8px 0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead style={{ background: '#f9fafb' }}>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                style={{
                  padding: '6px 10px',
                  textAlign: 'left',
                  fontWeight: 600,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: '#6b7280',
                  borderBottom: '1px solid #e5e7eb',
                  whiteSpace: 'nowrap'
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, ri) => (
            <tr key={ri}>
              {parseRow(row).map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    padding: '6px 10px',
                    borderBottom: '1px solid #f3f4f6',
                    color: '#374151',
                    verticalAlign: 'top'
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Split markdown into segments, rendering GFM tables as real HTML tables
 * and passing everything else through ReactMarkdown.
 */
const MarkdownContent = ({ content }) => {
  const segments = [];
  const lines = content.split('\n');
  let i = 0;
  let textBuffer = [];

  while (i < lines.length) {
    const line = lines[i];
    // Detect table: current line has pipes, next line is a separator (|---|)
    if (line.trim().startsWith('|') && lines[i + 1] && /^\|[-| :]+\|/.test(lines[i + 1].trim())) {
      // Flush text buffer
      if (textBuffer.length) {
        const text = textBuffer.join('\n');
        segments.push(
          <ReactMarkdown key={segments.length} components={MARKDOWN_COMPONENTS}>
            {text}
          </ReactMarkdown>
        );
        textBuffer = [];
      }
      // Collect table lines
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      segments.push(parseTable(tableLines, segments.length));
    } else {
      textBuffer.push(line);
      i++;
    }
  }

  // Flush remaining text
  if (textBuffer.length) {
    const text = textBuffer.join('\n');
    segments.push(
      <ReactMarkdown key={segments.length} components={MARKDOWN_COMPONENTS}>
        {text}
      </ReactMarkdown>
    );
  }

  return <>{segments}</>;
};
import {
  ChatBubble,
  ChatContent,
  ChatEmptyState,
  ChatHeader,
  ChatHeaderTitle,
  ChatInputArea,
  ChatMessages,
  ChatPanel,
  ChatTextarea,
  CloseBtn,
  DrawerResizeHandle,
  SendBtn,
  SuggestBtn,
  SuggestedQuestions,
  TypingIndicator
} from './style';

const SUGGESTED_QUESTIONS = [
  'What were the most recent actions on this project?',
  'Show me any failed operations in the last 7 days.',
  'Who made changes to reporting configurations?',
  'Summarise all status updates for the current period.'
];

const SparkleIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2 L13.5 9.5 L21 11 L13.5 12.5 L12 20 L10.5 12.5 L3 11 L10.5 9.5 Z" />
    <path d="M19 2 L19.7 4.3 L22 5 L19.7 5.7 L19 8 L18.3 5.7 L16 5 L18.3 4.3 Z" />
    <path d="M5 16 L5.5 17.5 L7 18 L5.5 18.5 L5 20 L4.5 18.5 L3 18 L4.5 17.5 Z" />
  </svg>
);

const SendIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const StopIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <rect x="4" y="4" width="16" height="16" rx="2" />
  </svg>
);

// Custom renderers for clean markdown output
const MARKDOWN_COMPONENTS = {
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', margin: '8px 0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => <thead style={{ background: '#f9fafb' }}>{children}</thead>,
  th: ({ children }) => (
    <th
      style={{
        padding: '6px 10px',
        textAlign: 'left',
        fontWeight: 600,
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: '#6b7280',
        borderBottom: '1px solid #e5e7eb',
        whiteSpace: 'nowrap'
      }}
    >
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td
      style={{
        padding: '6px 10px',
        borderBottom: '1px solid #f3f4f6',
        color: '#374151',
        verticalAlign: 'top'
      }}
    >
      {children}
    </td>
  ),
  tr: ({ children }) => <tr style={{ transition: 'background 0.1s' }}>{children}</tr>,
  p: ({ children }) => <p style={{ margin: '0 0 6px', lineHeight: 1.55 }}>{children}</p>,
  ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: '16px' }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: '3px', lineHeight: 1.5 }}>{children}</li>,
  strong: ({ children }) => (
    <strong style={{ fontWeight: 600, color: '#111827' }}>{children}</strong>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code
        style={{
          background: '#e5e7eb',
          padding: '1px 5px',
          borderRadius: '3px',
          fontSize: '11px',
          fontFamily: 'monospace'
        }}
      >
        {children}
      </code>
    ) : (
      <pre
        style={{
          background: '#f3f4f6',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          padding: '10px 12px',
          overflowX: 'auto',
          margin: '6px 0'
        }}
      >
        <code style={{ fontSize: '11px', fontFamily: 'monospace', color: '#374151' }}>
          {children}
        </code>
      </pre>
    ),
  h1: ({ children }) => (
    <p style={{ fontWeight: 700, fontSize: '14px', margin: '8px 0 4px', color: '#111827' }}>
      {children}
    </p>
  ),
  h2: ({ children }) => (
    <p style={{ fontWeight: 700, fontSize: '13px', margin: '6px 0 3px', color: '#111827' }}>
      {children}
    </p>
  ),
  h3: ({ children }) => (
    <p style={{ fontWeight: 600, fontSize: '13px', margin: '4px 0 2px', color: '#374151' }}>
      {children}
    </p>
  ),
  blockquote: ({ children }) => (
    <blockquote
      style={{
        borderLeft: '3px solid #d0ebff',
        margin: '6px 0',
        paddingLeft: '10px',
        color: '#6b7280',
        fontStyle: 'italic'
      }}
    >
      {children}
    </blockquote>
  )
};

/**
 * AiAnalyticsChat — slide-in chat panel for the Activity Log page.
 *
 * Props:
 *   filters         – object with active page filters (from_date, to_date, project_id,
 *                     period_id, user_id) forwarded to the backend so the AI only sees
 *                     the currently filtered logs.
 *   onClose         – called when the user closes the panel.
 *   initialQuestion – optional question to auto-send immediately on open.
 */
const AiAnalyticsChat = ({ filters = {}, onClose, initialQuestion, auditLogId = null }) => {
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant', content: string }
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);

  // Resize state
  const [width, setWidth] = useState(380);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);
  const handleRef = useRef(null);

  const onMouseDown = e => {
    dragging.current = true;
    startX.current = e.clientX;
    startW.current = width;
    if (handleRef.current) handleRef.current.dataset.dragging = 'true';
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = ev => {
      if (!dragging.current) return;
      const delta = startX.current - ev.clientX; // drag left = grow
      setWidth(Math.min(800, Math.max(320, startW.current + delta)));
    };
    const onUp = () => {
      dragging.current = false;
      if (handleRef.current) handleRef.current.dataset.dragging = 'false';
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const abortRef = useRef(null);
  const sentInitialRef = useRef(false);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Abort any in-flight stream on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const sendMessage = useCallback(
    async questionOverride => {
      const question = (questionOverride ?? input).trim();
      if (!question || isStreaming) return;

      setInput('');
      const userMsg = { role: 'user', content: question };
      const history = messages; // snapshot before we add the new user message
      setMessages(prev => [...prev, userMsg]);
      setIsStreaming(true);

      // Add an empty assistant message that we'll stream into
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const body = {
          question,
          history: history.map(m => ({ role: m.role, content: m.content })),
          ...(filters.from_date && { from_date: filters.from_date }),
          ...(filters.to_date && { to_date: filters.to_date }),
          ...(filters.project_id && { project_id: filters.project_id }),
          ...(filters.period_id && { period_id: filters.period_id }),
          ...(filters.user_id && { user_id: filters.user_id }),
          ...(auditLogId && { audit_log_id: auditLogId })
        };

        const response = await fetch(
          `${import.meta.env.VITE_BASE_URL.replace(/\/?$/, '/')}api/audit-logs/chat`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authStore.token}`,
              ...(localStorage.getItem('activeCompanyId') && {
                'X-Active-Company-Id': localStorage.getItem('activeCompanyId')
              })
            },
            body: JSON.stringify(body),
            signal: controller.signal
          }
        );

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(
            `Server error ${response.status}${text ? `: ${text.slice(0, 200)}` : ''}`
          );
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant') {
              updated[updated.length - 1] = {
                ...last,
                content: last.content + chunk
              };
            }
            return updated;
          });
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          // User cancelled — leave the partial message as-is
        } else {
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant') {
              updated[updated.length - 1] = {
                ...last,
                content:
                  (last.content || '') +
                  `\n\n_Error: ${err.message || 'Could not reach the AI service'}_`
              };
            }
            return updated;
          });
        }
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [input, isStreaming, messages, filters]
  );

  // Auto-send the initial question (e.g. from "Explain with AI" button)
  useEffect(() => {
    if (initialQuestion && !sentInitialRef.current) {
      sentInitialRef.current = true;
      // Defer by one tick so sendMessage closure has the fresh initial state
      const id = setTimeout(() => sendMessage(initialQuestion), 0);
      return () => clearTimeout(id);
    }
  }, [initialQuestion, sendMessage]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const handleStop = () => {
    abortRef.current?.abort();
  };

  const isEmpty = messages.length === 0;

  return (
    <ChatPanel $width={width}>
      <DrawerResizeHandle ref={handleRef} onMouseDown={onMouseDown} title="Drag to resize" />
      <ChatContent>
        {/* ── Header ─────────────────────────────────────────────────── */}
        <ChatHeader>
          <ChatHeaderTitle>
            <SparkleIcon />
            AI Analytics
          </ChatHeaderTitle>
          <CloseBtn onClick={onClose} title="Close AI Analytics">
            ×
          </CloseBtn>
        </ChatHeader>

        {/* ── Message list or empty state ─────────────────────────────── */}
        {isEmpty ? (
          <>
            <ChatEmptyState>
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#228be6"
                strokeWidth="1.5"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <p>
                Ask a question about the activity logs.
                <br />
                The AI will only see logs matching your current filters.
              </p>
            </ChatEmptyState>
            <SuggestedQuestions>
              {SUGGESTED_QUESTIONS.map(q => (
                <SuggestBtn key={q} onClick={() => sendMessage(q)}>
                  {q}
                </SuggestBtn>
              ))}
            </SuggestedQuestions>
          </>
        ) : (
          <ChatMessages>
            {messages.map((msg, i) => (
              <ChatBubble key={i} $role={msg.role}>
                {msg.role === 'user' ? (
                  msg.content
                ) : msg.content === '' && isStreaming ? (
                  <TypingIndicator>
                    <span />
                    <span />
                    <span />
                  </TypingIndicator>
                ) : (
                  <MarkdownContent content={msg.content} />
                )}
              </ChatBubble>
            ))}
            <div ref={messagesEndRef} />
          </ChatMessages>
        )}

        {/* ── Input area ──────────────────────────────────────────────── */}
        <ChatInputArea>
          <ChatTextarea
            ref={textareaRef}
            rows={1}
            placeholder="Ask about your activity logs…"
            value={input}
            disabled={isStreaming}
            onChange={e => {
              setInput(e.target.value);
              // Auto-grow
              e.target.style.height = 'auto';
              e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
            }}
            onKeyDown={handleKeyDown}
          />
          {isStreaming ? (
            <SendBtn onClick={handleStop} title="Stop generating">
              <StopIcon />
            </SendBtn>
          ) : (
            <SendBtn onClick={() => sendMessage()} disabled={!input.trim()} title="Send">
              <SendIcon />
            </SendBtn>
          )}
        </ChatInputArea>
      </ChatContent>
    </ChatPanel>
  );
};

export default AiAnalyticsChat;
